import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DayPost } from '../types/post';
import { 
  getDayPosts as getDayPostsService,
  getLast7Days,
  getLast28Days,
  getMoreDays,
  addNoteLog,
  addTrainingLog,
  addWellnessLog,
  addWellnessLogAndLinkToDayPost,
  calculateAndSavePeakScore,
  addPerformanceLog,
  addStepLog,
  addRecoveryLog,
  addNiggleLog,
  addHealthLog,
  addLogIDToDayPost,
  deleteNoteLog,
  deleteTrainingLog,
  deleteWellnessLog,
  deletePerformanceLog,
  deleteStepLog,
  deleteRecoveryLog,
  deleteNiggleLog,
  deleteHealthLog,
  removeLogIDFromDayPost,
  updateNoteLog,
  updateTrainingLog,
  updateWellnessLog,
  updatePerformanceLog,
  updateStepLog,
  updateRecoveryLog,
  updateNiggleLog,
  updateHealthLog,
  generateDateID 
} from '../services/dayPostService';

// Types for pending operations
export interface PendingLogOperation {
  id: string;
  userId: string;
  type: 'addLog' | 'deleteLog';
  logData?: any;
  logTypeKey: 'noteLogIDs' | 'trainingLogIDs' | 'wellnessLogIDs' | 'performanceLogIDs' | 'stepLogIDs' | 'recoveryLogIDs' | 'niggleLogIDs' | 'healthLogIDs';
  logType: 'note' | 'training' | 'wellness' | 'performance' | 'step' | 'recovery' | 'niggle' | 'health';
  dateID: string;
  createdAt: Date;
  retryCount: number;
  logId?: string; // Required for delete operations
  /** Training-log deletes only: the athlete's "delete linked sessions too?" answer. */
  deleteLinkedSessionsAndActivity?: boolean;
  /** Performance training-log deletes only: "Also delete N linked results?" answer. */
  deleteLinkedResults?: boolean;
}

// Type for cached day post data
interface CachedDayPostData {
  data: DayPost[];
  timestamp: number;
}

// Hook for day posts list management with offline-first approach
export function useDayPostList(options?: { userId?: string; disableCache?: boolean; disableAutoLoad?: boolean }) {
  const { currentUser } = useAuth();
  // Fallback to offline-cached user ID when offline (ensures cached data loads on dashboard)
  const getEffectiveUserId = useCallback(() => {
    // Explicit override if provided (e.g., admin viewing another user)
    if (options?.userId) {
      return options.userId;
    }
    try {
      // Lazy import to avoid circular deps at module init
      const { offlineAuthService } = require('../services/offlineAuthService');
      const cached = offlineAuthService.getCachedAuthState?.();
      return currentUser?.uid || cached?.userId || null;
    } catch {
      return currentUser?.uid || null;
    }
  }, [currentUser?.uid, options?.userId]);
  const [dayPosts, setDayPosts] = useState<DayPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [totalDaysLoaded, setTotalDaysLoaded] = useState(7);
  // Track which specific dateIDs have been loaded in this session
  const [sessionLoadedDateIds, setSessionLoadedDateIds] = useState<Set<string>>(new Set());

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Function to fetch day posts data
  const fetchDayPostsData = useCallback(async (useCache = true, dayCount = 7, append = false) => {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId) {
      setLoading(false);
      return;
    }

    const cacheEnabled = !options?.disableCache;
    const cacheKey = `day-posts-${effectiveUserId}-${dayCount}`;
    let cachedData: CachedDayPostData | null = null;
    let hasCachedData = false;
    
    // Always try to get cached data first (unless disabled)
    // For 28-day requests, also check if we have 7-day cache as fallback
    if (cacheEnabled) {
      try {
        let cached = localStorage.getItem(cacheKey);
        if (cached) {
          cachedData = JSON.parse(cached);
          hasCachedData = true;
        } else if (dayCount === 28) {
          // Fallback: check for 7-day cache when requesting 28 days
          const fallbackKey = `day-posts-${effectiveUserId}-7`;
          cached = localStorage.getItem(fallbackKey);
          if (cached) {
            cachedData = JSON.parse(cached);
            hasCachedData = true;
            // console.log('[DAYPOST_CACHE] Using 7-day cache as fallback for 28-day request');
          }
        }
      } catch (error) {
        // console.warn('[DAYPOST_CACHE] Failed to parse cached data:', error);
      }
    }
    
    // STEP 1: Load cached data immediately if available (for fast UI)
    // This ensures users see data quickly, regardless of online/offline status
    if (hasCachedData && cachedData && !append) {
      // console.log('[DAYPOST_CACHE] Loading cached data immediately for fast UI');
      if (!Array.isArray(cachedData.data)) {
        setError(new Error('[DAYPOST_CACHE] Corrupt cache – no data array'));
        setLoading(false);
        return;
      }
      
      setDayPosts(cachedData.data);
      setIsFromCache(true);
      setLastUpdated(new Date(cachedData.timestamp));
      setLoading(false); // Stop loading immediately when we have cached data
      setLoadingMore(false);
      setError(null);
      // Initialize session tracking with cached data dateIDs
      setSessionLoadedDateIds(new Set(cachedData.data.map(p => p.dateID)));
      
      // If offline, we're done - return early
      if (isOffline) {
        // console.log('[DAYPOST_CACHE] Offline mode - using cached data only');
        return;
      }
      
      // IMPORTANT: Always fetch fresh data in background when online
      // Remove the cache freshness check - we want the latest data
      // console.log('[DAYPOST_CACHE] Fetching fresh data in background to ensure latest updates');
    }

    // STEP 2: Always fetch fresh data from Firebase when online (cache-first, then fetch pattern)
    if (!isOffline) {
      try {
        let dateIds: string[];
        if (dayCount === 7 && !append) {
          // For regular refresh, get last 7 days plus any additional dates from current session
          dateIds = getLast7Days();
          
          // Add any currently loaded dates that fall outside the last 7 days
          const last7DaysSet = new Set(dateIds);
          const additionalDates = Array.from(sessionLoadedDateIds).filter(dateId => 
            !last7DaysSet.has(dateId)
          );
          
          if (additionalDates.length > 0) {
            dateIds = [...dateIds, ...additionalDates];
            // console.log('[DAYPOST_FETCH] Including', additionalDates.length, 'additional dates from session:', additionalDates);
          }
        } else if (dayCount === 7) {
          dateIds = getLast7Days();
        } else if (dayCount === 28) {
          dateIds = getLast28Days();
        } else {
          // For loading more days, get all days from 0 to dayCount
          dateIds = [];
          const today = new Date();
          for (let i = 0; i < dayCount; i++) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            dateIds.push(generateDateID(date));
          }
        }
        
        const posts = await getDayPostsService(effectiveUserId, dateIds);
        // console.log('[DAYPOST_FETCH] Fetched fresh data from Firebase');
        
        if (append) {
          // For loading more, only append the new posts that aren't already loaded
          const existingDateIds = dayPosts.map(p => p.dateID);
          const newPosts = posts.filter(p => !existingDateIds.includes(p.dateID));
          setDayPosts(prev => [...prev, ...newPosts]);
          // Track the new loaded dateIDs
          setSessionLoadedDateIds(prev => new Set([...Array.from(prev), ...newPosts.map(p => p.dateID)]));
        } else {
          // Preserve previously loaded days from the session when refetching
          // Fresh data always takes precedence over preserved data
          const freshDateIds = new Set(posts.map(p => p.dateID));
          const previouslyLoadedDays = dayPosts.filter(p => 
            !freshDateIds.has(p.dateID) && sessionLoadedDateIds.has(p.dateID)
          );
          
          // Merge fresh data with previously loaded data, sorted by dateID (newest first)
          // Fresh data comes first to ensure it takes precedence
          const mergedPosts = [...posts, ...previouslyLoadedDays]
            .sort((a, b) => b.dateID.localeCompare(a.dateID));
          
          setDayPosts(mergedPosts);
          // console.log('[DAYPOST_FETCH] Merged', posts.length, 'fresh posts with', previouslyLoadedDays.length, 'previously loaded days');
          // Update session tracking with all current dateIDs
          setSessionLoadedDateIds(new Set(mergedPosts.map(p => p.dateID)));
        }
        // Smart cache indicator management to prevent flicker
        if (!hasCachedData) {
          // No cached data was used, so this is fresh
          setIsFromCache(false);
        } else {
          // We used cached data initially, now we have fresh data
          const cachedCount = cachedData?.data?.length || 0;
          const freshCount = posts.length;
          
          // Only clear cache indicator if we have substantially more data
          // or if this was a 28-day request and we had 7-day cache (substantial difference expected)
          if (freshCount > cachedCount * 1.5 || (dayCount === 28 && cachedCount <= 7)) {
            setIsFromCache(false);
          } else {
            // Keep showing cache indicator if data is similar (prevents flicker)
            // console.log('[DAYPOST_CACHE] Keeping cache indicator - similar data received');
          }
        }
        setLastUpdated(new Date());
        setLoading(false);
        setLoadingMore(false);
        setError(null);

        // Cache the fresh data (unless disabled)
        if (cacheEnabled) {
          try {
            localStorage.setItem(cacheKey, JSON.stringify({
              data: posts,
              timestamp: Date.now()
            }));
            // console.log('[DAYPOST_CACHE] Cached fresh day posts');
          } catch (error) {
            // console.warn('[DAYPOST_CACHE] Failed to cache data:', error);
          }
        }
      } catch (error) {
        console.error('[DAYPOST_FETCH] Error fetching day posts:', error);
        
        // If we already loaded cached data, don't show error - keep the cached data
        if (hasCachedData && cachedData && !append) {
          // console.log('[DAYPOST_CACHE] Network error, but cached data already loaded');
          // Don't change anything - cached data is already displayed
        } else {
          // No cached data and network error - show error
          setError(error as Error);
          setLoading(false);
          setLoadingMore(false);
          
          // Try to use cached data as fallback if we haven't already
          if (cacheEnabled && hasCachedData && cachedData && !append) {
            // console.log('[DAYPOST_CACHE] Using cached data as fallback for network error');
            setDayPosts(cachedData.data);
            setIsFromCache(true);
            setLastUpdated(new Date(cachedData.timestamp));
            setError(null); // Clear error since we have fallback data
            // Initialize session tracking with fallback cached data dateIDs
            setSessionLoadedDateIds(new Set(cachedData.data.map(p => p.dateID)));
          }
        }
      }
    } else if ((!hasCachedData || !cacheEnabled) && !append) {
      // Offline with no cached data - this is the only time we should show "Loading..."
      setLoading(false);
      setError(new Error('No internet connection and no cached data available'));
    }
  }, [getEffectiveUserId, isOffline, dayPosts, options?.disableCache]);

  // Function to load more days
  const loadMoreDays = useCallback(async (daysToLoad: number = 7) => {
    const effectiveUserId = getEffectiveUserId();
    if (!effectiveUserId || loadingMore) {
      return;
    }
    
    setLoadingMore(true);
    setError(null);
    
    // Calculate the correct offset based on what we actually have loaded
    // Find the oldest dateID currently loaded to determine real offset
    const currentDateIds = dayPosts.map(p => p.dateID).sort((a, b) => b.localeCompare(a));
    const oldestLoadedDate = currentDateIds[currentDateIds.length - 1];
    
    let realOffset = 0;
    if (oldestLoadedDate) {
      // Calculate how many days back from today the oldest loaded date is
      const today = new Date();
      const oldestDate = new Date(
        parseInt(oldestLoadedDate.substring(0, 4)),
        parseInt(oldestLoadedDate.substring(4, 6)) - 1,
        parseInt(oldestLoadedDate.substring(6, 8))
      );
      realOffset = Math.floor((today.getTime() - oldestDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    } else {
      realOffset = totalDaysLoaded;
    }
    
    const newTotalDays = realOffset + daysToLoad;
    
    try {
      if (isOffline) {
        // Try to load from cache for more days (unless disabled)
        if (!options?.disableCache) {
          const effectiveUserId = getEffectiveUserId();
          const cacheKey = `day-posts-${effectiveUserId}-${newTotalDays}`;
          const cached = localStorage.getItem(cacheKey);
          if (cached) {
            const cachedData = JSON.parse(cached);
            if (Array.isArray(cachedData?.data)) {
              const existingDateIds = dayPosts.map(p => p.dateID);
              const newPosts = cachedData.data.filter((p: DayPost) => !existingDateIds.includes(p.dateID));
              setDayPosts(prev => [...prev, ...newPosts]);
              setTotalDaysLoaded(newTotalDays);
              // Track the new loaded dateIDs
              setSessionLoadedDateIds(prev => new Set([...Array.from(prev), ...newPosts.map((p: DayPost) => p.dateID)]));
            }
          }
        }
        setLoadingMore(false);
        return;
      }
      
      // Online: fetch additional days
      const additionalDateIds = getMoreDays(daysToLoad, realOffset);
      const newPosts = await getDayPostsService(effectiveUserId, additionalDateIds);
      
      setDayPosts(prev => [...prev, ...newPosts]);
      setTotalDaysLoaded(newTotalDays);
      setLoadingMore(false);
      // Track the new loaded dateIDs
      setSessionLoadedDateIds(prev => new Set([...Array.from(prev), ...newPosts.map(p => p.dateID)]));
      
      // console.log('[DAYPOST_FETCH] Loaded more days successfully');
    } catch (error) {
      console.error('[DAYPOST_FETCH] Error loading more days:', error);
      setError(error as Error);
      setLoadingMore(false);
    }
  }, [currentUser?.uid, isOffline, loadingMore, totalDaysLoaded, dayPosts, options?.disableCache, getEffectiveUserId]);

  // Load day posts data once on mount and whenever user navigates to Feed page
  useEffect(() => {
    if (getEffectiveUserId() && !options?.disableAutoLoad) {
      // Always fetch fresh data (useCache=false) to ensure latest updates
      // This implements the cache-first + background fetch pattern
      fetchDayPostsData(false);
    }
  }, [getEffectiveUserId, options?.disableAutoLoad]); // Only run when user id changes (including offline-cached)

  return {
    dayPosts,
    loading,
    loadingMore,
    error,
    isFromCache,
    isOffline,
    lastUpdated,
    fetchDayPostsData,
    loadMoreDays
  };
}

// Hook for adding logs with offline queue support
export function useDayPostSubmission(onSuccess?: () => Promise<void>) {
  const { currentUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Helper function to handle common log submission logic
  const submitLog = useCallback(async (
    logData: any,
    addLogFn: (userID: string, logData: any, dateID: string) => Promise<string>,
    logTypeKey: string,
    dateID: string,
    addToPendingQueue?: (operation: Omit<PendingLogOperation, 'id' | 'userId' | 'createdAt' | 'retryCount'>) => string | undefined
  ) => {
    if (!currentUser?.uid || !dateID) {
      throw new Error('Missing required data');
    }

    setSubmitting(true);
    setError(null);

    // If offline, add to pending queue instead of trying to submit
    if (isOffline) {
      try {
        if (addToPendingQueue) {
          addToPendingQueue({
            type: 'addLog',
            logData,
            logTypeKey: logTypeKey as any,
            dateID,
            logType: logTypeKey.replace('LogIDs', '') as PendingLogOperation['logType']
          });
          console.log('[DAYPOST_SUBMIT] Added to pending queue (offline)');
          setSubmitting(false);
          return { queued: true };
        } else {
          throw new Error('Offline queue not available');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        setSubmitting(false);
        throw err;
      }
    }

    // Online submission
    try {
      const logID = await addLogFn(currentUser.uid, logData, dateID);
      await addLogIDToDayPost(currentUser.uid, dateID, logID, logTypeKey as any);
      
      console.log('[DAYPOST_SUBMIT] Successfully submitted log');
      setSubmitting(false);
      
      if (onSuccess) {
        await onSuccess();
      }
      
      return { submitted: true, logID };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      setSubmitting(false);
      
      // If network error and we have pending queue, add to queue
      if (!navigator.onLine && addToPendingQueue) {
        try {
          addToPendingQueue({
            type: 'addLog',
            logData,
            logTypeKey: logTypeKey as any,
            dateID,
            logType: logTypeKey.replace('LogIDs', '') as PendingLogOperation['logType']
          });
          console.log('[DAYPOST_SUBMIT] Network failed, added to pending queue');
          setError('Added to queue - will submit when online');
          return { queued: true };
        } catch (queueErr) {
          console.error('[DAYPOST_SUBMIT] Failed to add to queue:', queueErr);
        }
      }
      
      throw err;
    }
  }, [currentUser, onSuccess, isOffline]);

  // Specific log submission methods - fixed parameter order to match Dashboard usage
  const submitNoteLog = useCallback(async (
    dateID: string,
    notes: string, 
    addToPendingQueue?: (operation: Omit<PendingLogOperation, 'id' | 'userId' | 'createdAt' | 'retryCount'>) => string | undefined
  ) => {
    return submitLog({ notes }, addNoteLog, 'noteLogIDs', dateID, addToPendingQueue);
  }, [submitLog]);

  const submitTrainingLog = useCallback(async (
    dateID: string,
    trainingLog: any, 
    addToPendingQueue?: (operation: Omit<PendingLogOperation, 'id' | 'userId' | 'createdAt' | 'retryCount'>) => string | undefined
  ) => {
    return submitLog(trainingLog, addTrainingLog, 'trainingLogIDs', dateID, addToPendingQueue);
  }, [submitLog]);

  const submitWellnessLog = useCallback(async (
    dateID: string,
    wellnessLog: any, 
    addToPendingQueue?: (operation: Omit<PendingLogOperation, 'id' | 'userId' | 'createdAt' | 'retryCount'>) => string | undefined,
    onPeakScoreComplete?: (result: { success: boolean; data?: any; error?: string }) => void | Promise<void>
  ) => {
    if (!currentUser?.uid || !dateID) {
      throw new Error('Missing required data');
    }

    setSubmitting(true);
    setError(null);

    // If offline, add to pending queue instead of trying to submit
    if (isOffline) {
      try {
        if (addToPendingQueue) {
          addToPendingQueue({
            type: 'addLog',
            logData: wellnessLog,
            logTypeKey: 'wellnessLogIDs',
            dateID,
            logType: 'wellness'
          });
          console.log('[DAYPOST_SUBMIT] Added wellness log to pending queue (offline)');
          setSubmitting(false);
          return { queued: true };
        } else {
          throw new Error('Offline queue not available');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        setSubmitting(false);
        throw err;
      }
    }

    // Online submission - save the wellness log and link it to the day post
    // quickly so the UI can immediately move on. The peak score calculation
    // (a slow Cloud Function call) runs in the background and notifies the
    // caller via `onPeakScoreComplete` when it finishes.
    try {
      const userId = currentUser.uid;
      const result = await addWellnessLogAndLinkToDayPost(userId, wellnessLog, dateID);
      console.log('[DAYPOST_SUBMIT] Successfully submitted wellness log');
      setSubmitting(false);

      if (onSuccess) {
        await onSuccess();
      }

      // Kick off peak score calculation without blocking the caller.
      void (async () => {
        try {
          const peakScoreResult = await calculateAndSavePeakScore(dateID);
          if (!peakScoreResult.success) {
            console.warn(
              '[DAYPOST_SUBMIT] Peak score calculation failed (background):',
              peakScoreResult.error,
            );
          } else {
            console.log(
              '[DAYPOST_SUBMIT] Peak score calculated (background):',
              peakScoreResult.data,
            );
          }
          if (onPeakScoreComplete) {
            await onPeakScoreComplete(peakScoreResult);
          }
        } catch (bgErr) {
          console.error('[DAYPOST_SUBMIT] Background peak score calculation failed:', bgErr);
          if (onPeakScoreComplete) {
            try {
              await onPeakScoreComplete({
                success: false,
                error: bgErr instanceof Error ? bgErr.message : 'Unknown error',
              });
            } catch (cbErr) {
              console.error('[DAYPOST_SUBMIT] onPeakScoreComplete callback error:', cbErr);
            }
          }
        }
      })();

      return { submitted: true, wellnessLogId: result.wellnessLogId };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      setSubmitting(false);
      
      // If network error and we have pending queue, add to queue
      if (!navigator.onLine && addToPendingQueue) {
        try {
          addToPendingQueue({
            type: 'addLog',
            logData: wellnessLog,
            logTypeKey: 'wellnessLogIDs',
            dateID,
            logType: 'wellness'
          });
          console.log('[DAYPOST_SUBMIT] Network failed, added wellness log to pending queue');
          setError('Added to queue - will submit when online');
          return { queued: true };
        } catch (queueErr) {
          console.error('[DAYPOST_SUBMIT] Failed to add to queue:', queueErr);
        }
      }
      
      throw err;
    }
  }, [currentUser, onSuccess, isOffline]);

  const submitPerformanceLog = useCallback(async (
    dateID: string,
    performanceLog: any, 
    addToPendingQueue?: (operation: Omit<PendingLogOperation, 'id' | 'userId' | 'createdAt' | 'retryCount'>) => string | undefined
  ) => {
    if (!currentUser?.uid) {
      throw new Error('Missing user ID');
    }

    // First submit the performance log
    const result = await submitLog(performanceLog, addPerformanceLog, 'performanceLogIDs', dateID, addToPendingQueue);
    
    // If a session was created and linked, update the session with the performance log ID
    if (performanceLog.linkedSessionID && result && 'submitted' in result && result.submitted && 'logID' in result) {
      try {
        const { sessionService } = await import('../services/sessionService');
        await sessionService.updateSession(
          currentUser.uid,
          performanceLog.linkedSessionID,
          {
            linkedPerformanceLogID: result.logID,
          }
        );
        console.log(
          `Successfully linked session ${performanceLog.linkedSessionID} to performance log ${result.logID}`
        );
      } catch (linkError) {
        console.error('Error linking session to performance log:', linkError);
        // Don't throw - performance log creation succeeded
      }
    }
    
    return result;
  }, [submitLog, currentUser]);

  const submitStepLog = useCallback(async (
    dateID: string,
    stepLog: any, 
    addToPendingQueue?: (operation: Omit<PendingLogOperation, 'id' | 'userId' | 'createdAt' | 'retryCount'>) => string | undefined
  ) => {
    return submitLog(stepLog, addStepLog, 'stepLogIDs', dateID, addToPendingQueue);
  }, [submitLog]);

  const submitRecoveryLog = useCallback(async (
    dateID: string,
    recoveryLog: any, 
    addToPendingQueue?: (operation: Omit<PendingLogOperation, 'id' | 'userId' | 'createdAt' | 'retryCount'>) => string | undefined
  ) => {
    return submitLog(recoveryLog, addRecoveryLog, 'recoveryLogIDs', dateID, addToPendingQueue);
  }, [submitLog]);

  const submitNiggleLog = useCallback(async (
    dateID: string,
    niggleLog: any, 
    addToPendingQueue?: (operation: Omit<PendingLogOperation, 'id' | 'userId' | 'createdAt' | 'retryCount'>) => string | undefined
  ) => {
    return submitLog(niggleLog, addNiggleLog, 'niggleLogIDs', dateID, addToPendingQueue);
  }, [submitLog]);

  const submitHealthLog = useCallback(async (
    dateID: string,
    healthLog: any, 
    addToPendingQueue?: (operation: Omit<PendingLogOperation, 'id' | 'userId' | 'createdAt' | 'retryCount'>) => string | undefined
  ) => {
    return submitLog(healthLog, addHealthLog, 'healthLogIDs', dateID, addToPendingQueue);
  }, [submitLog]);

  // Delete log functions
  const deleteLog = useCallback(async (
    logID: string,
    logType: string,
    dateID: string,
    deleteLogFn: (userID: string, logID: string) => Promise<void>
  ) => {
    if (!currentUser?.uid || !logID || !dateID) {
      throw new Error('Missing required data for delete operation');
    }

    setSubmitting(true);
    setError(null);

    try {
      // Delete the log from Firebase
      await deleteLogFn(currentUser.uid, logID);
      
      // Remove the log ID from the day post
      await removeLogIDFromDayPost(currentUser.uid, dateID, logID, logType as any);
      
      console.log('[DAYPOST_DELETE] Successfully deleted log');
      setSubmitting(false);
      
      if (onSuccess) {
        await onSuccess();
      }
      
      return { deleted: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      setSubmitting(false);
      throw err;
    }
  }, [currentUser, onSuccess]);

  // Update log functions
  const updateLog = useCallback(async (
    logID: string,
    updates: any,
    updateLogFn: (userID: string, logID: string, updates: any) => Promise<void>
  ) => {
    if (!currentUser?.uid || !logID) {
      throw new Error('Missing required data for update operation');
    }

    setSubmitting(true);
    setError(null);

    try {
      // Update the log in Firebase
      await updateLogFn(currentUser.uid, logID, updates);
      
      console.log('[DAYPOST_UPDATE] Successfully updated log');
      setSubmitting(false);
      
      if (onSuccess) {
        await onSuccess();
      }
      
      return { updated: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      setSubmitting(false);
      throw err;
    }
  }, [currentUser, onSuccess]);

  // Specific delete methods
  const deleteNoteLogById = useCallback(async (logID: string, dateID: string) => {
    return deleteLog(logID, 'noteLogIDs', dateID, deleteNoteLog);
  }, [deleteLog]);

  /**
   * Delete a training log. `deleteLinkedSessionsAndActivity` is the athlete's
   * answer to "Delete the linked sessions and activity data too?" (plan
   * decision 5, asked by `DeleteTrainingLogDialog`); `false` unlinks and keeps
   * the sessions so they reappear under "Sessions to log".
   */
  const deleteTrainingLogById = useCallback(async (
    logID: string,
    dateID: string,
    deleteLinkedSessionsAndActivity: boolean = false,
    addToPendingQueue?: (operation: Omit<PendingLogOperation, 'id' | 'userId' | 'createdAt' | 'retryCount'>) => string | undefined,
    /** Performance training logs: also delete the linked result logs (PerformanceLog.md §4). */
    deleteLinkedResults: boolean = false,
  ) => {
    const queueDelete = () => {
      addToPendingQueue?.({
        type: 'deleteLog',
        logId: logID,
        logTypeKey: 'trainingLogIDs',
        dateID,
        logType: 'training',
        deleteLinkedSessionsAndActivity,
        deleteLinkedResults,
      });
    };

    if ((isOffline || !navigator.onLine) && addToPendingQueue) {
      queueDelete();
      return { queued: true as const };
    }

    try {
      return await deleteLog(logID, 'trainingLogIDs', dateID, (uid, id) =>
        deleteTrainingLog(uid, id, deleteLinkedSessionsAndActivity, {
          deleteLinkedResults,
        }),
      );
    } catch (err) {
      if (!navigator.onLine && addToPendingQueue) {
        queueDelete();
        setError('Added to queue - will delete when online');
        return { queued: true as const };
      }
      throw err;
    }
  }, [deleteLog, isOffline]);

  const deleteWellnessLogById = useCallback(async (logID: string, dateID: string) => {
    return deleteLog(logID, 'wellnessLogIDs', dateID, deleteWellnessLog);
  }, [deleteLog]);

  const deletePerformanceLogById = useCallback(async (logID: string, dateID: string) => {
    return deleteLog(logID, 'performanceLogIDs', dateID, deletePerformanceLog);
  }, [deleteLog]);

  const deleteStepLogById = useCallback(async (logID: string, dateID: string) => {
    return deleteLog(logID, 'stepLogIDs', dateID, deleteStepLog);
  }, [deleteLog]);

  const deleteRecoveryLogById = useCallback(async (logID: string, dateID: string) => {
    return deleteLog(logID, 'recoveryLogIDs', dateID, deleteRecoveryLog);
  }, [deleteLog]);

  const deleteNiggleLogById = useCallback(async (logID: string, dateID: string) => {
    return deleteLog(logID, 'niggleLogIDs', dateID, deleteNiggleLog);
  }, [deleteLog]);

  const deleteHealthLogById = useCallback(async (logID: string, dateID: string) => {
    return deleteLog(logID, 'healthLogIDs', dateID, deleteHealthLog);
  }, [deleteLog]);

  // Specific update methods
  const updateNoteLogById = useCallback(async (logID: string, updates: Partial<Omit<import('../types/post').NoteLog, 'timestamp'>>) => {
    return updateLog(logID, updates, updateNoteLog);
  }, [updateLog]);

  const updateTrainingLogById = useCallback(async (logID: string, updates: Partial<Omit<import('../types/post').TrainingLog, 'timestamp'>>) => {
    return updateLog(logID, updates, updateTrainingLog);
  }, [updateLog]);

  const updateWellnessLogById = useCallback(async (logID: string, updates: Partial<Omit<import('../types/post').WellnessLog, 'timestamp'>>) => {
    return updateLog(logID, updates, updateWellnessLog);
  }, [updateLog]);

  const updatePerformanceLogById = useCallback(async (logID: string, updates: Partial<Omit<import('../types/post').PerformanceLog, 'timestamp'>>) => {
    return updateLog(logID, updates, updatePerformanceLog);
  }, [updateLog]);

  const updateStepLogById = useCallback(async (logID: string, updates: Partial<Omit<import('../types/post').StepLog, 'timestamp'>>) => {
    return updateLog(logID, updates, updateStepLog);
  }, [updateLog]);

  const updateRecoveryLogById = useCallback(async (logID: string, updates: Partial<Omit<import('../types/post').RecoveryLog, 'timestamp'>>) => {
    return updateLog(logID, updates, updateRecoveryLog);
  }, [updateLog]);

  const updateNiggleLogById = useCallback(async (logID: string, updates: Partial<Omit<import('../types/post').NiggleLog, 'timestamp'>>) => {
    return updateLog(logID, updates, updateNiggleLog);
  }, [updateLog]);

  const updateHealthLogById = useCallback(async (logID: string, updates: Partial<Omit<import('../types/post').HealthLog, 'timestamp'>>) => {
    return updateLog(logID, updates, updateHealthLog);
  }, [updateLog]);

  return {
    submitNoteLog,
    submitTrainingLog,
    submitWellnessLog,
    submitPerformanceLog,
    submitStepLog,
    submitRecoveryLog,
    submitNiggleLog,
    submitHealthLog,
    deleteNoteLogById,
    deleteTrainingLogById,
    deleteWellnessLogById,
    deletePerformanceLogById,
    deleteStepLogById,
    deleteRecoveryLogById,
    deleteNiggleLogById,
    deleteHealthLogById,
    updateNoteLogById,
    updateTrainingLogById,
    updateWellnessLogById,
    updatePerformanceLogById,
    updateStepLogById,
    updateRecoveryLogById,
    updateNiggleLogById,
    updateHealthLogById,
    submitting,
    error,
    isOffline
  };
}

// Hook for managing pending day post operations (offline queue)
export function usePendingDayPostOperations(onSyncComplete?: () => Promise<void>) {
  const { currentUser } = useAuth();
  const [pendingItems, setPendingItems] = useState<PendingLogOperation[]>([]);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);

  const PENDING_DAYPOST_KEY = 'pending-daypost-operations';

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      // Don't call syncPendingOperations here - let the auto-sync effect handle it
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load pending items from localStorage on mount
  useEffect(() => {
    loadPendingItems();
  }, []);

  const loadPendingItems = useCallback(() => {
    try {
      const stored = localStorage.getItem(PENDING_DAYPOST_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert stored dates back to Date objects
        const items = parsed.map((item: any) => ({
          ...item,
          createdAt: new Date(item.createdAt),
          // Validate required fields
          id: item.id || `recovered_${Date.now()}`,
          userId: item.userId || '',
          retryCount: typeof item.retryCount === 'number' ? item.retryCount : 0
        }));
        setPendingItems(items);
        console.log('[PENDING_DAYPOST] Loaded pending items:', items.length);
      }
    } catch (error) {
      console.error('[PENDING_DAYPOST] Failed to load pending items:', error);
    }
  }, []);

  const savePendingItems = useCallback((items: PendingLogOperation[]) => {
    try {
      localStorage.setItem(PENDING_DAYPOST_KEY, JSON.stringify(items));
    } catch (error) {
      console.error('[PENDING_DAYPOST] Failed to save pending items:', error);
    }
  }, []);

  const addPendingItem = useCallback((operation: Omit<PendingLogOperation, 'id' | 'userId' | 'createdAt' | 'retryCount'>) => {
    if (!currentUser?.uid) return;

    // Map logTypeKey to logType
    const logTypeMap: Record<string, PendingLogOperation['logType']> = {
      'noteLogIDs': 'note',
      'trainingLogIDs': 'training',
      'wellnessLogIDs': 'wellness',
      'performanceLogIDs': 'performance',
      'stepLogIDs': 'step',
      'recoveryLogIDs': 'recovery',
      'niggleLogIDs': 'niggle',
      'healthLogIDs': 'health'
    };

    const newItem: PendingLogOperation = {
      ...operation,
      id: `daypost_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId: currentUser.uid,
      createdAt: new Date(),
      retryCount: 0,
      logType: logTypeMap[operation.logTypeKey]
    };

    setPendingItems(prevItems => {
      const updatedItems = [...prevItems, newItem];
      savePendingItems(updatedItems);
      return updatedItems;
    });
    
    console.log('[PENDING_DAYPOST] Added pending operation:', newItem.id);
    return newItem.id;
  }, [currentUser?.uid, savePendingItems]);

  const removePendingItem = useCallback((itemId: string) => {
    setPendingItems(prevItems => {
      const updatedItems = prevItems.filter(item => item.id !== itemId);
      // Save immediately after state update
      savePendingItems(updatedItems);
      return updatedItems;
    });
    
    console.log('[PENDING_DAYPOST] Removed pending operation:', itemId);
  }, [savePendingItems]);

  const syncPendingOperations = useCallback(async () => {
    if (isOffline || syncingRef.current || pendingItems.length === 0) {
      return;
    }

    syncingRef.current = true;
    setSyncing(true);

    const successfullyProcessed: string[] = [];

    for (const item of pendingItems) {
      try {
        if (!currentUser?.uid) continue;

        if (item.type === 'addLog') {
          console.log('[PENDING_DAYPOST] Processing add log operation:', item.id, 'logType:', item.logTypeKey);
          
          let logID: string;
          
          // Use logTypeKey instead of function name (which gets minified)
          if (item.logTypeKey === 'noteLogIDs') {
            logID = await addNoteLog(currentUser.uid, item.logData, item.dateID);
          } else if (item.logTypeKey === 'trainingLogIDs') {
            logID = await addTrainingLog(currentUser.uid, item.logData, item.dateID);
          } else if (item.logTypeKey === 'wellnessLogIDs') {
            logID = await addWellnessLog(currentUser.uid, item.logData, item.dateID);
          } else if (item.logTypeKey === 'performanceLogIDs') {
            logID = await addPerformanceLog(currentUser.uid, item.logData, item.dateID);
          } else if (item.logTypeKey === 'stepLogIDs') {
            logID = await addStepLog(currentUser.uid, item.logData, item.dateID);
          } else if (item.logTypeKey === 'recoveryLogIDs') {
            logID = await addRecoveryLog(currentUser.uid, item.logData, item.dateID);
          } else if (item.logTypeKey === 'niggleLogIDs') {
            logID = await addNiggleLog(currentUser.uid, item.logData, item.dateID);
          } else if (item.logTypeKey === 'healthLogIDs') {
            logID = await addHealthLog(currentUser.uid, item.logData, item.dateID);
          } else {
            throw new Error(`Unknown log type: ${item.logTypeKey}`);
          }
          
          // Add the log ID to the day post
          await addLogIDToDayPost(currentUser.uid, item.dateID, logID, item.logTypeKey);
          successfullyProcessed.push(item.id);
        } else if (item.type === 'deleteLog' && item.logId) {
          console.log('[PENDING_DAYPOST] Processing delete log operation:', item.id, 'logType:', item.logTypeKey);
          
          // Use logTypeKey to determine which delete function to call
          if (item.logTypeKey === 'noteLogIDs') {
            await deleteNoteLog(currentUser.uid, item.logId);
          } else if (item.logTypeKey === 'trainingLogIDs') {
            await deleteTrainingLog(
              currentUser.uid,
              item.logId,
              item.deleteLinkedSessionsAndActivity === true,
              { deleteLinkedResults: item.deleteLinkedResults === true },
            );
          } else if (item.logTypeKey === 'wellnessLogIDs') {
            await deleteWellnessLog(currentUser.uid, item.logId);
          } else if (item.logTypeKey === 'performanceLogIDs') {
            await deletePerformanceLog(currentUser.uid, item.logId);
          } else if (item.logTypeKey === 'stepLogIDs') {
            await deleteStepLog(currentUser.uid, item.logId);
          } else if (item.logTypeKey === 'recoveryLogIDs') {
            await deleteRecoveryLog(currentUser.uid, item.logId);
          } else if (item.logTypeKey === 'niggleLogIDs') {
            await deleteNiggleLog(currentUser.uid, item.logId);
          } else if (item.logTypeKey === 'healthLogIDs') {
            await deleteHealthLog(currentUser.uid, item.logId);
          } else {
            throw new Error(`Unknown log type: ${item.logTypeKey}`);
          }
          
          // Remove the log ID from the day post
          await removeLogIDFromDayPost(currentUser.uid, item.dateID, item.logId, item.logTypeKey);
          successfullyProcessed.push(item.id);
        }
      } catch (error) {
        console.error('[PENDING_DAYPOST] Error processing operation:', error);
        
        // Increment retry count
        setPendingItems(prevItems => {
          const updatedItems = prevItems.map(pendingItem => 
            pendingItem.id === item.id 
              ? { ...pendingItem, retryCount: (pendingItem.retryCount || 0) + 1 }
              : pendingItem
          );
          // Save immediately after state update
          savePendingItems(updatedItems);
          return updatedItems;
        });
      }
    }

    // Remove successfully processed items in batch
    if (successfullyProcessed.length > 0) {
      setPendingItems(prevItems => {
        const remainingItems = prevItems.filter(item => !successfullyProcessed.includes(item.id));
        // Save immediately after state update
        savePendingItems(remainingItems);
        return remainingItems;
      });
      
      // Call the callback to refetch day post data after successful sync
      if (onSyncComplete) {
        try {
          await onSyncComplete();
          console.log('[PENDING_DAYPOST] Successfully refetched day post data after sync');
        } catch (error) {
          console.error('[PENDING_DAYPOST] Failed to refetch day post data after sync:', error);
        }
      }
    }

    syncingRef.current = false;
    setSyncing(false);
  }, [isOffline, pendingItems, removePendingItem, savePendingItems, currentUser?.uid, onSyncComplete]);

  // Auto-sync when conditions are right
  useEffect(() => {
    if (!isOffline && pendingItems.length > 0 && !syncingRef.current) {
      const timer = setTimeout(() => {
        syncPendingOperations();
      }, 1000); // Small delay to avoid rapid syncing

      return () => clearTimeout(timer);
    }
  }, [isOffline, pendingItems.length, syncPendingOperations]);

  return {
    pendingItems,
    addPendingItem,
    removePendingItem,
    syncPendingOperations,
    syncing,
    isOffline
  };
}

// Helper hook for date formatting and utilities
export function useDayPostHelpers() {
  const formatDate = useCallback((dateID: string) => {
    const year = parseInt(dateID.substring(0, 4));
    const month = parseInt(dateID.substring(4, 6));
    const day = parseInt(dateID.substring(6, 8));
    const date = new Date(year, month - 1, day);
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  }, []);

  const formatShortDate = useCallback((dateID: string) => {
    const year = parseInt(dateID.substring(0, 4));
    const month = parseInt(dateID.substring(4, 6));
    const day = parseInt(dateID.substring(6, 8));
    const date = new Date(year, month - 1, day);
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  }, []);

  const isToday = useCallback((dateID: string) => {
    const today = generateDateID(new Date());
    return dateID === today;
  }, []);

  return {
    formatDate,
    formatShortDate,
    isToday
  };
} 